/**
 * router/index.js
 *
 * Vue Router configuration for Vitrina Salon public pages
 */

import { createRouter, createWebHistory } from 'vue-router';

import { HOST_IS_SALON } from '@/js/utils/hostForm.js';
import { resolveSalonId } from '@/js/utils/salonBootstrap.js';

console.log("[router] Initializing vitrina router...");

// The two host forms of VITRINA_HOST_MODEL.md §2 need two DIFFERENT route
// tables, and which one to build is known before the first navigation: the edge
// has already stamped `route-base` and `salon-id` into the shell.
//
// `/` used to be the domain landing UNCONDITIONALLY, so on a customer's own
// domain the edge resolved the salon, stamped the id, and the SPA then painted
// "your business could have a page on this domain" over it — the salon was
// reachable at no URL at all (René, 2026-08-21). The router's own comment said
// this wiring was deferred to #16; this is it.
//
// Built from the metas rather than from `window.__SALON_ID__` because the route
// table is constructed at IMPORT time, before main.js assigns that global.
const ROOT_IS_THE_SALON = HOST_IS_SALON && !!resolveSalonId();

// The in-salon pages. Identical in both forms — only where they are MOUNTED
// differs, so they are declared once. `home` is a function because the /about
// redirect has to name the right target per form.
const inSalonChildren = (home) => [
  {
    path: '',
    name: 'Vitrina Home',
    component: () => import('@/views/salon/Home.vue'),
  },
  {
    path: 'services',
    name: 'Vitrina Services',
    component: () => import('@/views/salon/Services.vue'),
  },
  // /about REDIRECTS home and has no view behind it (#42, closed
  // 2026-08-08). It was parked in August because About.vue was entirely
  // mock data — three invented stylists, "Beautiful Salon", a 555 phone,
  // a New York address — live under real salons' brands. It is not
  // coming back: the approved storefront design is a ONE-PAGE site, and
  // everything /about used to promise now lives there for real (the
  // salon's own story, its team section, its gallery). The redirect
  // stays because printed and shared /about links must land somewhere.
  {
    path: 'about',
    name: 'Vitrina About',
    redirect: home,
  },
  {
    path: 'book',
    name: 'Vitrina Booking',
    component: () => import('@/views/salon/Booking.vue'),
    meta: { requiresAuth: false },
  },
  // Unknown page UNDER a resolved salon (/glam-studio/no-existe, or
  // lasherz.com/no-existe): a real 404 inside the salon's own chrome, with a
  // button back to its home. In the slug form this only renders when the
  // parent's beforeEnter passed, i.e. the first segment DID resolve to a
  // salon; an unresolved first segment matches the same route, fails the
  // guard, and lands on the domain landing instead — the distinction René
  // asked for (2026-07-26).
  {
    path: ':pathMatch(.*)*',
    name: 'Vitrina Page Not Found',
    component: () => import('@/views/salon/PageNotFound.vue'),
  },
];

// ROOT IS THE SALON — a customer's own domain or a per-salon subdomain. The
// host owns everything: `/` is the salon's home and `/book` is its booking
// page. There is no slug and no landing route, because on this host there is
// nothing else to land on: an unknown path is a 404 INSIDE the salon, not an
// advert for SALON on a domain the customer pays for.
const rootIsSalonRoutes = [
  {
    path: '/',
    component: () => import('@/layouts/SalonLayout.vue'),
    children: inSalonChildren(() => '/'),
  },
];

// ROOT IS AN INDEX — the portfolio domains. `/` is the marketing landing and
// the first path segment is the salon's slug.
const rootIsIndexRoutes = [
  // A FULL LOAD of "/" never reaches this route — the edge serves its own
  // static twin (vitrina-gae-cdn/static/domain-landing.html) and the SPA
  // does not boot. This exists for in-app navigation back to the root from
  // a salon page, which used to land on the generic NotFound card: same
  // URL, two different pages depending on how you arrived.
  {
    path: '/',
    name: 'Fallback',
    component: () => import('@/views/DomainLanding.vue'),
  },
  // Clean-slug salon routes (#16). This site serves ONE salon — the edge (or
  // the localhost localStorage override) already resolved WHICH salon at
  // boot, so any single-segment path is just the salon's address. Without a
  // resolved salon id the fallback page renders instead.
  {
    path: '/:salonSlug',
    component: () => import('@/layouts/SalonLayout.vue'),
    beforeEnter: () => (window.__SALON_ID__ ? true : { name: 'Fallback' }),
    children: inSalonChildren((to) => `/${to.params.salonSlug}`),
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    ...(ROOT_IS_THE_SALON ? rootIsSalonRoutes : rootIsIndexRoutes),
    // Legacy salon routes by simpleId — deprecated in favor of clean slugs;
    // kept while the transition soaks so old links keep resolving.
    {
      path: '/salon/:salonSlug/:simpleId',
      component: () => import('@/layouts/SalonLayout.vue'),
      children: [
        { 
          path: '', 
          name: 'Salon Home',
          component: () => import('@/views/salon/Home.vue') 
        },
        { 
          path: 'services', 
          name: 'Salon Services',
          component: () => import('@/views/salon/Services.vue') 
        },
        // Same on the legacy tree (#42) — see the clean-slug twin above.
        {
          path: 'about',
          name: 'Salon About',
          redirect: (to) => `/salon/${to.params.salonSlug}/${to.params.simpleId}`,
        },
        {
          path: 'book',
          name: 'Salon Booking',
          component: () => import('@/views/salon/Booking.vue'),
          meta: { requiresAuth: false }  // Auth checked at Step 4, not route level
        },
        // Same in-salon 404 for the legacy /salon/:slug/:simpleId group.
        {
          path: ':pathMatch(.*)*',
          name: 'Salon Page Not Found',
          component: () => import('@/views/salon/PageNotFound.vue')
        }
      ]
    },
    // Nothing resolved at all → the landing (no redirect loop, no
    // marketplace). By the time a path reaches here its first segment did not
    // match a salon, so the honest answer is "this domain could be yours",
    // not a 404: there is no site to send the visitor back to. A bad page
    // INSIDE a real salon is handled by that salon's own 404 child above.
    //
    // Omitted in the root form: there, `/`'s own :pathMatch child already owns
    // every unknown path, and two wildcards competing for the same URL is a
    // ranking question nobody should have to reason about.
    ...(ROOT_IS_THE_SALON
      ? []
      : [
          {
            path: '/:pathMatch(.*)*',
            name: 'NotFound',
            component: () => import('@/views/DomainLanding.vue'),
          },
        ]),
  ],
});

// Workaround for https://github.com/vitejs/vite/issues/11804
router.onError((err, to) => {
  if (err?.message?.includes?.('Failed to fetch dynamically imported module')) {
    if (!localStorage.getItem('vuetify:dynamic-reload')) {
      console.log('[router] Reloading page to fix dynamic import error');
      localStorage.setItem('vuetify:dynamic-reload', 'true');
      location.assign(to.fullPath);
    } else {
      console.error('[router] Dynamic import error, reloading page did not fix it', err);
    }
  } else {
    console.error("[router] ", err);
  }
});

router.isReady().then(() => {
  console.debug("[router] Router ready");
  localStorage.removeItem('vuetify:dynamic-reload');
});

router.beforeEach((to, from, next) => {
  console.debug('[router] Navigating to:', to.fullPath);
  
  // All routes in vitrina are public salon pages
  // No authentication required at route level
  next();
});

export default router;
